import { useEffect, useState } from "react";
import { Link , useNavigate} from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Bell,
  FileText,
  MessageSquare,
  Award,
  UserPlus,
  CheckCircle2,
  BookOpen,
  Users,
  FolderKanban,
  Trophy,
  Settings,
  Mail,
  Check,
  X,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Notification,
  getUserNotifications,
  getUnreadNotificationCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  formatNotificationTime,
  NotificationType,
  deleteNotification,
} from "@/lib/notifications";
import { acceptTeamInvitation, rejectTeamInvitation } from "@/lib/team-invitations";
import { api } from "@/lib/api";

interface NotificationsDropdownProps {
  userId: string;
}

export function NotificationsDropdown({
  userId,
}: NotificationsDropdownProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [processingInvitation, setProcessingInvitation] = useState<string | null>(null);

  // Fetch notifications
  const fetchNotifications = async () => {
    if (!userId) return;

    try {
      const [notifs, count] = await Promise.all([
        getUserNotifications(userId, { limit: 20 }),
        getUnreadNotificationCount(userId),
      ]);

      // Filter out notifications for already-processed team invitations
      const validNotifications = await Promise.all(
        notifs.map(async (notif) => {
          if (notif.type === "team" && notif.metadata?.type === "team_invitation" && notif.metadata?.invitation_id) {
            try {
              const invitation = await api<{ status: string }>(`/team-invitations/${notif.metadata.invitation_id}`);
              
              // If invitation doesn't exist or is already processed, delete the notification
              if (!invitation || (invitation.status === "accepted" || invitation.status === "rejected")) {
                await deleteNotification(notif.id, userId);
                return null;
              }
            } catch {
              // If invitation doesn't exist, delete the notification
              await deleteNotification(notif.id, userId);
              return null;
            }
          }
          return notif;
        })
      );

      setNotifications(validNotifications.filter(Boolean) as Notification[]);
      setNotificationCount(count);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchNotifications();
      
      // Poll for new notifications every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [userId]);

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await markNotificationAsRead(notificationId, userId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );
      setNotificationCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead(userId);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setNotificationCount(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const handleAcceptInvitation = async (invitationId: string, notificationId: string) => {
    setProcessingInvitation(invitationId);
    try {
      const success = await acceptTeamInvitation(invitationId, userId);
      if (success) {
        await markNotificationAsRead(notificationId, userId);
        await deleteNotification(notificationId, userId);
        await fetchNotifications();
      }
    } catch (error) {
      console.error("Error accepting invitation:", error);
    } finally {
      setProcessingInvitation(null);
    }
  };

  const handleRejectInvitation = async (invitationId: string, notificationId: string) => {
    setProcessingInvitation(invitationId);
    try {
      const success = await rejectTeamInvitation(invitationId, userId);
      if (success) {
        await markNotificationAsRead(notificationId, userId);
        await deleteNotification(notificationId, userId);
        await fetchNotifications();
      }
    } catch (error) {
      console.error("Error rejecting invitation:", error);
    } finally {
      setProcessingInvitation(null);
    }
  };

  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case "message":
        return MessageSquare;
      case "invitation":
        return UserPlus;
      case "submission":
        return FileText;
      case "assignment":
        return BookOpen;
      case "course":
        return BookOpen;
      case "class":
        return BookOpen;
      case "achievement":
        return Trophy;
      case "badge":
        return Award;
      case "team":
        return Users;
      case "project":
        return FolderKanban;
      default:
        return Bell;
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="relative">
          <Bell className="h-5 w-5" />
          {notificationCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-medium text-white">
              {notificationCount > 9 ? "9+" : notificationCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-[500px] overflow-y-auto">
        <div className="flex items-center justify-between p-3 border-b">
          <h3 className="font-semibold text-sm">Notifications</h3>
          {notificationCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={handleMarkAllAsRead}
            >
              Mark all read
            </Button>
          )}
        </div>
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No notifications
          </div>
        ) : (
          <div className="divide-y">
            {notifications.map((notification) => {
              const Icon = getNotificationIcon(notification.type);
              const isInvitation =
                notification.type === "team" &&
                notification.metadata?.type === "team_invitation";
              const invitationId = notification.metadata?.invitation_id;

              return (
                <div
                  key={notification.id}
                  className={cn(
                    "p-3 hover:bg-muted/50 transition-colors",
                    !notification.read && "bg-blue-50/50 dark:bg-blue-950/20"
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="text-sm font-medium">{notification.title}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {notification.message}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatNotificationTime(notification.created_at)}
                          </p>
                        </div>
                        {!notification.read && (
                          <div className="h-2 w-2 rounded-full bg-blue-500 shrink-0 mt-1" />
                        )}
                      </div>
                      {isInvitation && invitationId && (
                        <div className="flex gap-2 mt-2">
                          <Button
                            size="sm"
                            variant="default"
                            className="h-7 text-xs"
                            onClick={() =>
                              handleAcceptInvitation(invitationId, notification.id)
                            }
                            disabled={processingInvitation === invitationId}
                          >
                            {processingInvitation === invitationId ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <>
                                <Check className="h-3 w-3 mr-1" />
                                Accept
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() =>
                              handleRejectInvitation(invitationId, notification.id)
                            }
                            disabled={processingInvitation === invitationId}
                          >
                            <X className="h-3 w-3 mr-1" />
                            Reject
                          </Button>
                        </div>
                      )}
                      {notification.link && !isInvitation && (
                        <Link
                          to={notification.link}
                          onClick={() => handleMarkAsRead(notification.id)}
                          className="text-xs text-blue-600 hover:underline mt-1 inline-block"
                        >
                          View
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {notifications.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/notifications" className="w-full text-center">
                View all notifications
              </Link>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
